import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import styled from 'styled-components';

import type { ChatMessage } from '../../types';
import defaults from '../../translations/en.json';
import { MessageList } from './MessageList';

type PastChatViewProps = {
  /** When, who, how long and how many tokens, for the conversation or the one question shown. */
  summaryLabel?: string | null;
  messages: ChatMessage[];
  isLoading?: boolean;
  error?: string | null;
};

const Root = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background-color: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

const SummaryChip = styled.div`
  flex: none;
  align-self: center;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  color: ${(props) =>
    props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
  background-color: ${(props) =>
    props.theme.palette.background.dark1 ||
    props.theme.palette.background.default};
`;

const Loading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 4px 12px;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
  font-size: 0.8rem;
`;

const Footer = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px;
  font-size: 0.9375rem;
  color: ${(props) =>
    props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
  border-top: 1px solid ${(props) => props.theme.palette.divider || '#555'};
`;

/**
 * A previous conversation (anyone's), or one of its questions, full size —
 * read only, nothing to send.
 */
const PastChatView = ({
  summaryLabel,
  messages,
  isLoading,
  error,
}: PastChatViewProps) => (
  <Root data-testid="maia-past-chat">
    {isLoading ? (
      <Loading>
        <CircularProgress size={16} />
        <span>{defaults.loadingConversation}</span>
      </Loading>
    ) : (
      <MessageList
        messages={messages}
        header={
          summaryLabel ? (
            <SummaryChip data-testid="maia-past-summary">
              {summaryLabel}
            </SummaryChip>
          ) : null
        }
      />
    )}
    {error ? <ErrorText role="alert">{error}</ErrorText> : null}
    <Footer>{defaults.readOnlyFooter}</Footer>
  </Root>
);

export default PastChatView;
